import { Select, Text, Tooltip } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { PreviewAsPill, ShortcutHint, ShortcutKey } from './FieldBindingEditor.styled.ts';
import type { InvestorOption } from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';

interface Props {
	options: InvestorOption[];
	value: string | null;
	onChange: (value: string | null) => void;
	onDropdownOpen: () => void;
	isFetching: boolean;
	disabled: boolean;
	/** Whether ←/→ currently step the recipient. The keycaps are only shown where they do. */
	shortcutsActive: boolean;
}

// The far-right "Preview as <investor>" control: a static label plus the
// borderless investor Select, wrapped in the pill so the two read as one.
export const PreviewAsSelect = ({
	options,
	value,
	onChange,
	onDropdownOpen,
	isFetching,
	disabled,
	shortcutsActive,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	// One option is the sample investor alone: nowhere to step yet, so no keycaps.
	const showShortcutHint = shortcutsActive && options.length > 1;
	return (
		<PreviewAsPill>
			{showShortcutHint && (
				<Tooltip
					label={tMipManage(
						'master_documents.field_binding.investor_select.shortcut_hint',
						{ defaultValue: 'Use ← and → to step between recipients' }
					)}
				>
					{/* Hidden from assistive tech, which would read the glyphs as punctuation;
					    `aria-keyshortcuts` on the Select states the same thing properly. */}
					<ShortcutHint aria-hidden data-testid="field-binding-investor-shortcut-hint">
						<ShortcutKey>←</ShortcutKey>
						<ShortcutKey>→</ShortcutKey>
					</ShortcutHint>
				</Tooltip>
			)}
			<Text size="sm" c="dimmed">
				{tMipManage('master_documents.field_binding.investor_select.preview_as')}
			</Text>
			<Select
				data-testid="field-binding-investor-select"
				variant="unstyled"
				w={220}
				{...(shortcutsActive ? { 'aria-keyshortcuts': 'ArrowLeft ArrowRight' } : {})}
				// The surrounding pill is the only visible boundary. Set the
				// input's border via the `styles` prop (inline) so it wins over
				// Mantine's dark focus border, which otherwise reads as a
				// doubled box nested inside the pill.
				styles={(theme) => ({
					input: {
						fontWeight: 700,
						color: theme.colors.dark[5],
						border: 'none',
					},
				})}
				// There is ALWAYS a preview recipient — the sample investor when no real one is
				// picked — so `null` is not a state this control should be able to reach.
				// Mantine defaults `allowDeselect` to true, so clicking the already-selected
				// recipient fired onChange(null): the catalogue then answered with no securities
				// and no schemes, the scope card vanished, and every scoped chip in the Word
				// editor reverted to its humanised name.
				allowDeselect={false}
				data={options}
				value={value}
				onChange={onChange}
				onDropdownOpen={onDropdownOpen}
				nothingFoundMessage={
					isFetching
						? tMipManage('master_documents.field_binding.investor_select.loading')
						: tMipManage('master_documents.field_binding.investor_select.no_results')
				}
				disabled={disabled}
			/>
		</PreviewAsPill>
	);
};
